/**
 * Helper para extraer texto de un PDF usando pdfjs-dist.
 *
 * Se usa lazy import para mantener el chunk fuera del bundle inicial —
 * pdfjs-dist + worker pesan ~600KB.
 */

export async function extractPdfText(file: File): Promise<string> {
  const pdfjs = await import("pdfjs-dist");
  // pdfjs-dist requiere un Worker. En Vite, importamos el worker como URL.
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    // Worker servido desde el bundle. Vite resuelve `?url` en build; el
    // tipo del módulo está declarado en src/vite-env.d.ts.
    const workerUrl = (await import("pdfjs-dist/build/pdf.worker.mjs?url")).default;
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
  }

  const buf = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buf }).promise;

  const pages: string[] = [];
  for (let i = 1; i <= doc.numPages; i += 1) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    // Reconstrucción robusta: agrupamos los items por su coordenada Y
    // (transform[5]) y dentro de cada línea los ordenamos por X
    // (transform[4]). Esto evita que pdfjs entregue items en un orden
    // arbitrario que rompa el parser (caso real: el extractor con
    // `hasEOL` ponía "TRASPASO SPEI INBURED" en la misma línea que su
    // monto, pero los parsers de BBVA/Inbursa/Konfio que se auditaron
    // contra los PDFs reales usan la agrupación por Y).
    const byY = new Map<number, Array<{ x: number; str: string }>>();
    for (const item of content.items) {
      if (!("str" in item)) continue;
      const transform = (item as { transform?: number[] }).transform;
      if (!transform) continue;
      const y = Math.round(transform[5] ?? 0);
      const x = transform[4] ?? 0;
      const str = item.str ?? "";
      const arr = byY.get(y);
      if (arr) arr.push({ x, str });
      else byY.set(y, [{ x, str }]);
    }
    const sortedY = [...byY.keys()].toSorted((a, b) => b - a);
    const lines = sortedY.map((y) =>
      byY
        .get(y)!
        .toSorted((a, b) => a.x - b.x)
        .map((it) => it.str)
        .join(" "),
    );
    pages.push(lines.join("\n"));
  }
  return pages.join("\n\n");
}
